'use client';

import { Typography } from '@mui/material';
import {
  BiometricEvidenceDialog,
  CaptureViewer,
  EvidenceProvenance,
  RequestLinkAction,
  TCaptureEntry,
} from '@verifime/components';
import { ReactElement, ReactNode } from 'react';
import { TRequestState } from './requestState';

export type TIdPortraitEvidenceProps = {
  open: boolean;
  onClose: () => void;
  /**
   * The already-fetched portrait. Supplied by the card, not fetched again by this view: see
   * `useIdPortraitEvidence`'s docblock for why one fetch has to serve both the button's gate and
   * this dialog's content.
   */
  portraitUrl?: string;
  /** Shared with the card rather than fetched again, so the two can never disagree. */
  requestState: TRequestState;
  /** The dialog's buttons. The request link is added by this view, on the left of them. */
  actions?: ReactNode;
  onError?: (error: unknown) => void;
  'data-testid'?: string;
};

/** Measured off the frozen prototype, the same box `BiometricEvidence` uses for this capture. */
const DOCUMENT_PORTRAIT_BOX = { width: 156, height: 196 };

/**
 * States where the portrait came from, since nothing else on this panel does: there is no face
 * check, no comparison and no liveness determination behind a `NotCompleted` biometric level, so
 * without this sentence the picture would sit unexplained beside a status line saying the check
 * itself was never done.
 */
const PORTRAIT_SOURCE_NOTE =
  'The portrait shown here was read from the identity document your customer provided.';

/**
 * The ID portrait, on its own, for the biometric card's `NotCompleted` level.
 *
 * **Not `BiometricEvidence` with a level swapped in.** That dialog composes findings, an outcome
 * reason and a face-check capture slot, none of which exist for a customer who never completed the
 * face check. This view reuses the same `BiometricEvidenceDialog` shell instead, carrying only what
 * `NotCompleted` genuinely has: the identity document portrait and its provenance.
 *
 * **`level` is fixed at `NotCompleted`, not a prop.** The one caller of this view is the card's
 * `NotCompleted` button, and letting the level vary would let a caller open this dialog for a level
 * it was never built to explain.
 *
 * **The status line is overridden.** With the heading changed to "ID portrait", the level's own
 * label ("Not completed") would read as the portrait itself being incomplete. It is not: the
 * portrait is present, which is the entire reason this dialog is reachable. See
 * `BiometricEvidenceDialog`'s `statusOverride` for the mechanism.
 */
export default function IdPortraitEvidence({
  open,
  onClose,
  portraitUrl,
  requestState,
  actions,
  onError,
  'data-testid': dataTestId,
}: Readonly<TIdPortraitEvidenceProps>): ReactElement {
  const captures: TCaptureEntry[] = [
    {
      id: 'document-portrait',
      label: 'ID portrait',
      box: DOCUMENT_PORTRAIT_BOX,
      // Missing rather than dropped if the portrait is somehow absent by the time this renders
      // (the card withholds the button for that case, but a customer change while the dialog is
      // still open is the one path that could still land here): the slot then reads "Not yet
      // available" instead of quietly disappearing.
      capture: portraitUrl ? { kind: 'capture', src: portraitUrl } : { kind: 'missing' },
    },
  ];

  return (
    <BiometricEvidenceDialog
      open={open}
      onClose={onClose}
      level="NotCompleted"
      heading="ID portrait"
      statusOverride="Biometric checks not completed"
      data-testid={dataTestId}
      media={
        <CaptureViewer
          captures={captures}
          onCaptureError={() => onError?.(new Error('ID portrait failed to load'))}
        />
      }
      actions={
        // Two nodes puts the link on the left and the buttons hard right; one node leaves the
        // buttons exactly where they sit with a link present, matching `BiometricEvidence`'s own
        // footer. The link is offered only when a task is open to be completed.
        requestState.requestUrl ? (
          <>
            <RequestLinkAction
              url={requestState.requestUrl}
              onCopyError={(error) => onError?.(error)}
            />
            {actions}
          </>
        ) : (
          actions
        )
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ textWrap: 'pretty' }}>
        {PORTRAIT_SOURCE_NOTE}
      </Typography>
      <EvidenceProvenance
        // Always dashed: `NotCompleted` means the customer never finished the biometric check, so
        // there is no completion timestamp to show, unlike `requestedOn`/`requestedBy` below, which
        // come from the face check task history and can be genuinely populated.
        completedOn={undefined}
        requestedOn={requestState.requestedOn}
        requestedBy={requestState.requestedBy}
      />
    </BiometricEvidenceDialog>
  );
}
